import type {
  ComponentBlock,
  Ingredient,
  IngredientGroup,
  Recipe,
  RecipeComponent,
} from './types';
import { INGREDIENT_GROUP_LABELS, ingredientGroupLabel } from './types';
import { netFromGross, netPriceOf } from './costing';
import { normalizeText, parseImportNumber } from './importer';
import { leerCantidad } from './cantidades';
import { matchIngredient, normalizeIngredientName } from './recipeProposal';
import type { IngredientInput } from './validation';
import { dimensionOf, isPurchaseUnit, toBase, UNITS, type PurchaseUnit, type Unit } from './units';

/**
 * Importacion masiva del catalogo: ingredientes y recetas desde una hoja de
 * calculo.
 *
 * Es el mayor bloqueo para dar de alta un restaurante: nadie teclea 200
 * ingredientes a mano. Alex lo pidio el 12 de agosto.
 *
 * Principios de diseño, aprendidos del importador de ventas:
 *
 * 1. NO exigimos un formato exacto. Las columnas se reconocen por el nombre
 *    de la cabecera, en cualquier orden, con o sin acentos, y admitiendo
 *    varios sinonimos ("producto"/"ingrediente"/"nombre").
 * 2. NADA se escribe sin que el usuario vea antes lo que va a pasar. Este
 *    modulo solo LEE y devuelve un plan: que se crea, que se actualiza y
 *    que filas no se entienden y por que.
 * 3. Los errores son por fila y con el numero de fila del Excel, para poder
 *    corregirlos en la hoja sin adivinar.
 * 4. Una fila mala no invalida el fichero: se importa lo bueno y se informa
 *    del resto.
 */

/* ------------------------------------------------------------------ */
/* Reconocimiento de columnas                                          */
/* ------------------------------------------------------------------ */

export type IngredientField =
  | 'id'
  | 'name'
  | 'brand'
  | 'presentation'
  | 'group'
  | 'supplier'
  | 'purchaseQty'
  | 'purchaseUnit'
  | 'netPrice'
  | 'grossPrice'
  | 'vatPct'
  | 'density';

export type RecipeField =
  | 'recipeName'
  | 'code'
  | 'kind'
  | 'group'
  | 'yieldCostBase'
  | 'yieldQty'
  | 'yieldUnit'
  | 'portions'
  | 'safetyMarginPct'
  | 'componentName'
  | 'qty'
  | 'unit'
  | 'wastePct'
  | 'block'
  | 'steps'
  /** Plantilla de Alex (oct 2026): precio de venta con IVA, solo platos. */
  | 'salePrice';

/**
 * Sinonimos aceptados por campo. El primero de cada lista es el nombre
 * "oficial" que sale en la plantilla que descarga el usuario.
 */
const INGREDIENT_HEADERS: Array<{ field: IngredientField; words: string[] }> = [
  /*
   * "Id Alexia" es la columna que anyade la EXPORTACION de ingredientes: con
   * ella la resubida actualiza por id y se puede renombrar un producto en el
   * Excel sin que se convierta en un duplicado. OJO: nada de poner 'id' a
   * secas como sinonimo — el matcher usa includes() y pescaria "unidad".
   */
  { field: 'id', words: ['id alexia'] },
  { field: 'name', words: ['producto', 'ingrediente', 'nombre', 'articulo', 'descripcion'] },
  { field: 'brand', words: ['marca'] },
  { field: 'presentation', words: ['presentacion', 'envase', 'formato'] },
  { field: 'group', words: ['grupo', 'familia', 'categoria'] },
  { field: 'supplier', words: ['proveedor', 'distribuidor'] },
  { field: 'purchaseQty', words: ['cantidad de compra', 'cantidad compra', 'cantidad', 'contenido'] },
  { field: 'purchaseUnit', words: ['unidad de compra', 'unidad compra', 'unidad', 'medida'] },
  /*
   * El bruto va ANTES que el neto a proposito: el matcher usa includes(), y
   * "Precio con IVA" contiene "precio". Y los dos van antes que el IVA, que
   * si no se quedaria con la cabecera "Precio sin IVA" (contiene "iva").
   *
   * "Precio" a secas cae en el neto, que es lo que traen los ficheros viejos
   * y las exportaciones anteriores al precio unico.
   */
  {
    field: 'grossPrice',
    words: [
      'precio con iva', 'precio con impuestos', 'precio bruto',
      'coste con iva', 'costo con iva', 'pvp con iva',
    ],
  },
  {
    field: 'netPrice',
    words: ['precio sin iva', 'precio sin impuestos', 'precio neto', 'precio', 'coste', 'costo'],
  },
  { field: 'vatPct', words: ['iva', 'impuesto'] },
  { field: 'density', words: ['densidad'] },
];

const RECIPE_HEADERS: Array<{ field: RecipeField; words: string[] }> = [
  { field: 'recipeName', words: ['receta', 'plato', 'elaboracion'] },
  { field: 'code', words: ['codigo', 'cod'] },
  { field: 'kind', words: ['tipo'] },
  { field: 'group', words: ['grupo de carta', 'grupo', 'seccion', 'categoria'] },
  /*
   * ANTES que "Rendimiento", que si no se lo queda por el includes(). Es la
   * casilla "Calcular el coste por unidad de rendimiento, en lugar de por
   * porcion" de la ficha, que hasta ahora solo se podia marcar a mano: quien
   * importaba 90 recetas tenia que entrar una por una.
   */
  {
    field: 'yieldCostBase',
    words: [
      'coste por unidad de rendimiento', 'coste por rendimiento',
      'costo por rendimiento', 'por rendimiento',
    ],
  },
  { field: 'yieldQty', words: ['rendimiento', 'produccion', 'cantidad producida'] },
  { field: 'yieldUnit', words: ['unidad de rendimiento', 'unidad rendimiento'] },
  { field: 'portions', words: ['raciones', 'porciones'] },
  { field: 'safetyMarginPct', words: ['margen de seguridad', 'margen seguridad'] },
  { field: 'componentName', words: ['ingrediente', 'componente', 'producto'] },
  { field: 'qty', words: ['cantidad neta', 'cantidad'] },
  { field: 'unit', words: ['unidad'] },
  { field: 'wastePct', words: ['merma'] },
  { field: 'block', words: ['bloque', 'tipo de linea'] },
  { field: 'steps', words: ['elaboracion paso a paso', 'pasos', 'preparacion'] },
  { field: 'salePrice', words: ['precio de venta', 'pvp'] },
];

/**
 * Empareja cada columna del fichero con un campo. Recorre las reglas EN
 * ORDEN y se queda con la primera que encaja, asi que las cabeceras mas
 * especificas ("cantidad de compra") tienen que ir antes que las genericas
 * ("cantidad"). Un campo solo se asigna una vez: si el fichero trae dos
 * columnas parecidas, gana la primera.
 */
export function matchHeaders<F extends string>(
  headers: string[],
  rules: Array<{ field: F; words: string[] }>,
): Array<F | undefined> {
  const usados = new Set<F>();
  return headers.map((header) => {
    const limpio = normalizeText(header);
    if (limpio === '') return undefined;
    for (const { field, words } of rules) {
      if (usados.has(field)) continue;
      if (words.some((w) => limpio === w || limpio.startsWith(`${w} `) || limpio.includes(w))) {
        usados.add(field);
        return field;
      }
    }
    return undefined;
  });
}

export const matchIngredientHeaders = (headers: string[]) =>
  matchHeaders(headers, INGREDIENT_HEADERS);

export const matchRecipeHeaders = (headers: string[]) => matchHeaders(headers, RECIPE_HEADERS);

/* ------------------------------------------------------------------ */
/* Utilidades de lectura                                               */
/* ------------------------------------------------------------------ */

const celda = <F extends string>(row: string[], cols: Array<F | undefined>, field: F): string =>
  (row[cols.indexOf(field)] ?? '').trim();

/** Reconoce una unidad escrita de cualquier manera: "Kg", "kilos", "L", "lt". */
export function parseUnit(raw: string): Unit | undefined {
  const limpio = normalizeText(raw);
  if (limpio === '') return undefined;

  const directo = (Object.keys(UNITS) as Unit[]).find(
    (u) => u === limpio || normalizeText(UNITS[u].label) === limpio,
  );
  if (directo) return directo;

  const alias: Record<string, Unit> = {
    kilo: 'kg', kilos: 'kg', kilogramo: 'kg', kilogramos: 'kg', kgs: 'kg',
    gramo: 'g', gramos: 'g', gr: 'g', grs: 'g',
    litro: 'l', litros: 'l', lt: 'l', lts: 'l',
    mililitro: 'ml', mililitros: 'ml', cc: 'ml',
    centilitro: 'cl', centilitros: 'cl',
    libra: 'lb', libras: 'lb', lbs: 'lb',
    onza: 'oz', onzas: 'oz', ozs: 'oz',
    pieza: 'pz', piezas: 'pz', pza: 'pz', pzas: 'pz', unidad: 'pz', unidades: 'pz', ud: 'pz', uds: 'pz', u: 'pz',
    docenas: 'docena', dz: 'docena',
    // Galon US y onza liquida (plantillas de Alex, oct 2026). Ojo: «galón»
    // tambien es una presentacion; aqui solo llegan columnas de UNIDAD.
    galon: 'gal', galones: 'gal', gals: 'gal', gallon: 'gal', gallons: 'gal',
    'fl oz s': 'floz', 'fl ozs': 'floz', 'oz fl': 'floz', 'onza liquida': 'floz', 'onzas liquidas': 'floz',
    'fluid ounce': 'floz', 'fluid ounces': 'floz',
    // La plantilla de recetas de Alex (oct 2026) abrevia las medidas de cocina.
    tazas: 'taza', cda: 'cucharada', cdas: 'cucharada', cucharadas: 'cucharada',
    cdita: 'cucharadita', cditas: 'cucharadita', cucharaditas: 'cucharadita',
  };
  return alias[limpio];
}

/** Reconoce el grupo de ingrediente por su etiqueta o su clave. */
export function parseIngredientGroup(raw: string): string | undefined {
  const limpio = normalizeText(raw);
  if (limpio === '') return undefined;
  const claves = Object.keys(INGREDIENT_GROUP_LABELS) as IngredientGroup[];
  const porClave = claves.find((k) => normalizeText(k) === limpio);
  if (porClave) return porClave;
  const porEtiqueta = claves.find((k) => normalizeText(INGREDIENT_GROUP_LABELS[k]) === limpio);
  if (porEtiqueta) return porEtiqueta;
  // Un grupo que no reconocemos se respeta tal cual: el tenant puede tener
  // grupos propios, y no vamos a tirar la fila por eso.
  return raw.trim();
}

/**
 * Casilla de si/no de una hoja de calculo. Alex propuso 0/1, pero por ahi
 * llegan tambien "si", "x", "true" y "verdadero" segun quien rellene.
 * Devuelve undefined si la celda esta vacia, para poder distinguir "el usuario
 * ha dicho que no" de "el usuario no ha dicho nada".
 */
export function parseSiNo(raw: string): boolean | undefined {
  const v = normalizeText(raw);
  if (v === '') return undefined;
  if (['1', 'si', 'sí', 'x', 'true', 'verdadero', 'v', 'y', 'yes'].includes(v)) return true;
  if (['0', 'no', 'false', 'falso', 'f', 'n'].includes(v)) return false;
  return undefined;
}

/* ------------------------------------------------------------------ */
/* Ingredientes                                                        */
/* ------------------------------------------------------------------ */

export interface ImportIssue {
  /** Fila tal y como se numera en el Excel (con cabecera = 1). */
  row: number;
  message: string;
  /** De quién es la fila: el producto, o «Plato · ingrediente» en recetas. */
  name?: string;
  /**
   * Fase 7: el arreglo que la previa puede aplicar ahí mismo, sin volver al
   * Excel. Sin él, la fila se arregla en el archivo, como hasta ahora.
   */
  fix?: ImportFix;
}

/**
 * Arreglos que propone el importador (fase 7, decidido con Alex el 10 oct):
 * «12,5 pesos» → «Sí, $12.50»; una unidad que no entiende → chips; un
 * ingrediente que ya tienes → «Actualizar su precio / Es otra, créala»; uno
 * parecido → «¿Es tu Crema ácida?»; una cantidad sin unidad → «¿Son 180 g?»;
 * una receta sin tipo que usan otras → «¿Es una preparación?».
 */
export type ImportFix =
  | { kind: 'precio'; raw: string; value?: number }
  | { kind: 'unidad'; raw: string; options: Unit[] }
  | {
      kind: 'existe';
      existingId: string;
      existingName: string;
      existing: { netPrice?: number; purchaseQty?: number; purchaseUnit?: Unit; presentation?: string };
      newPrice?: number;
      newQty: number;
      newUnit: PurchaseUnit;
    }
  | { kind: 'parecido'; raw: string; candidates: Array<{ ingredientId: string; name: string }> }
  | { kind: 'cantidad'; raw: string; qty: number; unit: Unit }
  | { kind: 'tipo'; recipeName: string; suggested: Recipe['kind']; usadaPor: number }
  // «1.500 kg»: ¿mil quinientos o kilo y medio? (revisión del 14 oct 2026)
  | { kind: 'cuantos'; raw: string; opciones: number[] };

/** Cómo leer las cantidades del Excel: con el separador decimal del país de la cuenta. */
export interface OpcionesCantidad {
  decimal?: ',' | '.';
}

/** Lo que el usuario contestó a cada arreglo, por número de fila del Excel. */
export interface IngredientRowCorrection {
  /** «Sí, $12.50» o lo que escribió él. En la columna de precio que trajera la fila. */
  precio?: number;
  purchaseUnit?: PurchaseUnit;
  /** «Actualizar su precio» / «Es otra, créala». */
  existente?: 'actualizar' | 'crear';
  /** «¿Es tu …?»: el id elegido, o 'crear'. */
  parecido?: string;
  /** «¿Son 1.500 o 1,5?»: la cantidad de compra elegida. */
  purchaseQty?: number;
}
export type IngredientCorrections = Record<number, IngredientRowCorrection>;

export interface RecipeRowCorrection {
  /** «Sí, es esa» / «Elegir otra». */
  ingredientId?: string;
  /** «Crearla nueva»: se crea «por confirmar». */
  crear?: boolean;
  qty?: number;
  unit?: Unit;
}
export interface RecipeCorrections {
  filas?: Record<number, RecipeRowCorrection>;
  /** Por nombre normalizado de la receta: «Sí, es una preparación». */
  tipos?: Record<string, Recipe['kind']>;
}

/**
 * Cantidad de una celda: con el formato del país si se sabe (`leerCantidad`);
 * si no, como siempre.
 */
function leerCantidadDe(raw: string, opciones: OpcionesCantidad): number | undefined {
  return opciones.decimal ? leerCantidad(raw, opciones.decimal)?.valor : parseImportNumber(raw);
}

/** «1.500»: ¿1500 o 1,5? Se pregunta con las dos lecturas (la del país primero). */
function dudaDeCantidad(raw: string, opciones: OpcionesCantidad): { message: string; fix: ImportFix } | undefined {
  if (!opciones.decimal) return undefined;
  const leida = leerCantidad(raw, opciones.decimal);
  if (!leida || leida.otra == null) return undefined;
  return {
    message: `En la cantidad dice «${raw.trim()}». ¿Cuánto es?`,
    fix: { kind: 'cuantos', raw: raw.trim(), opciones: [leida.valor, leida.otra] },
  };
}

/** Precio escrito con texto («12,5 pesos», «MXN 40»): se lee, pero se pregunta. */
function precioConTexto(raw: string): boolean {
  return /[a-z]/i.test(raw);
}

/** Lee un precio escrito como sea: quita moneda y palabras y entiende la coma. */
export function parsePrecioLibre(raw: string): number | undefined {
  const limpio = raw.replace(/\b(pesos?|mxn|usd|d[oó]lares?|euros?|eur|cop|clp|pen|ars|soles?|mn|m\.n\.)\b/gi, ' ').replace(/[$€]/g, ' ').trim();
  if (!limpio || /[a-z]/i.test(limpio)) return undefined;
  const value = parseImportNumber(limpio.replace(/\s+/g, ''));
  return value != null && value > 0 ? value : undefined;
}

export interface IngredientDraft {
  /** Fila original del Excel, contando la cabecera como fila 1. */
  row: number;
  name: string;
  brand?: string;
  presentation?: string;
  group: string;
  supplierName?: string;
  purchaseQty: number;
  purchaseUnit: PurchaseUnit;
  /** Siempre sin IVA: si la fila venia con el precio con IVA, ya esta convertido. */
  netPrice?: number;
  pricePending?: boolean;
  vatPct: number;
  densityGPerMl?: number;
  /** Id del ingrediente existente que se va a actualizar, si lo hay. */
  existingId?: string;
  /**
   * Nombre con el que venía en el Excel cuando el usuario dijo «Sí, es esa»
   * a un ingrediente parecido: la previa ofrece recordarlo como alias.
   */
  aliasDe?: string;
}

export interface IngredientImportResult {
  /** Ingredientes nuevos que se crearan. */
  nuevos: IngredientDraft[];
  /** Ingredientes que ya existen y se actualizaran. */
  actualizados: IngredientDraft[];
  /** Proveedores citados en el fichero que todavia no existen. */
  proveedoresNuevos: string[];
  problemas: ImportIssue[];
  /** Campos que no hemos sabido localizar en la cabecera. */
  columnasNoEncontradas: IngredientField[];
}

/** Clave de identidad de un ingrediente: nombre + marca. */
export const ingredientKey = (name: string, brand?: string): string =>
  `${normalizeText(name)}|${normalizeText(brand ?? '')}`;

/**
 * ¿El Excel trae otro precio para un ingrediente que ya tienes? Se compara
 * por kilo, litro o pieza: «$14.50 el saco de 20 kg» y «$0.725/kg» son el
 * mismo precio. Sin precio en alguno de los dos, no hay nada que preguntar.
 */
function precioDistinto(
  actual: Partial<Pick<Ingredient, 'netPrice' | 'purchaseQty' | 'purchaseUnit' | 'pricePending'>>,
  nuevo: Pick<IngredientDraft, 'netPrice' | 'purchaseQty' | 'purchaseUnit'>,
): boolean {
  if (nuevo.netPrice == null || actual.netPrice == null || actual.pricePending || !actual.purchaseUnit || !(actual.purchaseQty! > 0)) return false;
  if (dimensionOf(actual.purchaseUnit) !== dimensionOf(nuevo.purchaseUnit)) return true;
  const antes = actual.netPrice / toBase(actual.purchaseQty!, actual.purchaseUnit);
  const ahora = nuevo.netPrice / toBase(nuevo.purchaseQty, nuevo.purchaseUnit);
  return Math.abs(ahora / antes - 1) > 0.005;
}

export function buildIngredientImport(
  rows: string[][],
  cols: Array<IngredientField | undefined>,
  existentes: Array<Pick<Ingredient, 'ingredientId' | 'name' | 'brand'> & Partial<Pick<Ingredient, 'netPrice' | 'purchaseQty' | 'purchaseUnit' | 'presentation' | 'pricePending' | 'aliases'>>> = [],
  proveedores: Array<{ supplierId: string; name: string }> = [],
  correcciones: IngredientCorrections = {},
  opcionesCantidad: OpcionesCantidad = {},
): IngredientImportResult {
  const nuevos: IngredientDraft[] = [];
  const actualizados: IngredientDraft[] = [];
  const problemas: ImportIssue[] = [];
  const proveedoresNuevos = new Set<string>();

  // Fase 7: solo el nombre y en qué se mide. Sin cantidad, el formato es 1.
  const obligatorias: IngredientField[] = ['name', 'purchaseUnit'];
  const columnasNoEncontradas = obligatorias.filter((f) => !cols.includes(f));
  // El precio puede llegar después por factura o edición manual.
  if (columnasNoEncontradas.length > 0) {
    return { nuevos, actualizados, proveedoresNuevos: [], problemas, columnasNoEncontradas };
  }

  const porClave = new Map(existentes.map((i) => [ingredientKey(i.name, i.brand), i]));
  const porId = new Map(existentes.map((i) => [i.ingredientId, i]));
  const proveedoresConocidos = new Set(proveedores.map((p) => normalizeText(p.name)));
  const vistos = new Set<string>();

  rows.forEach((row, index) => {
    // +2: la cabecera es la fila 1 y el usuario cuenta desde 1.
    const numero = index + 2;
    const name = celda(row, cols, 'name');
    if (name === '') return; // fila vacia: ni se cuenta como problema

    if (name.length > 120) {
      problemas.push({
        row: numero,
        message: `«Producto» tiene ${name.length} caracteres; el máximo es 120. Acorta el nombre.`,
      });
      return;
    }

    const correccion = correcciones[numero] ?? {};
    const qtyCelda = celda(row, cols, 'purchaseQty');
    const dudaQty = qtyCelda !== '' && correccion.purchaseQty == null ? dudaDeCantidad(qtyCelda, opcionesCantidad) : undefined;
    if (dudaQty) {
      problemas.push({ row: numero, name, message: dudaQty.message, fix: dudaQty.fix });
      return;
    }
    const purchaseQty = correccion.purchaseQty ?? (qtyCelda === '' ? 1 : leerCantidadDe(qtyCelda, opcionesCantidad));
    if (purchaseQty === undefined || purchaseQty <= 0) {
      problemas.push({
        row: numero,
        name,
        message: `"${name}": la cantidad de compra tiene que ser un número mayor que 0.`,
      });
      return;
    }

    const unidadCelda = celda(row, cols, 'purchaseUnit');
    const purchaseUnit = correccion.purchaseUnit ?? parseUnit(unidadCelda);
    // "una cucharada" no es un formato de compra: se pregunta con chips.
    if (!isPurchaseUnit(purchaseUnit)) {
      problemas.push({
        row: numero,
        name,
        message: unidadCelda === ''
          ? 'Falta en qué se mide.'
          : `No entiendo la unidad «${unidadCelda}». ¿En qué se mide?`,
        fix: { kind: 'unidad', raw: unidadCelda, options: ['kg', 'l', 'pz'] },
      });
      return;
    }

    /*
     * Precio: el usuario rellena la columna que tenga delante, sin IVA
     * (albaran) o con IVA (tique), y aqui se guarda SIEMPRE el neto.
     *
     * Las tres reglas son de Alex y todas dicen lo mismo: antes que guardar
     * un numero que no sabemos si es el bueno, se para la fila.
     *  1. Con las dos columnas rellenas no elegimos una: si 42,50 y 47,90 no
     *     cuadran hay un problema real debajo (un IVA mal puesto, un precio
     *     viejo) y callarlo es guardar un dato falso.
     *  2. Con el precio con IVA y el IVA vacio no hay forma de sacar el neto.
     *  3. Cambiar el IVA mas adelante no toca el neto: se recalcula el bruto.
     *     Eso lo garantiza el modelo, que solo guarda el neto.
     */
    const vatRaw = parseImportNumber(celda(row, cols, 'vatPct'));
    /*
     * Fase 7: «12,5 pesos» o «$ 40 MXN» se leen, pero se pregunta («¿Es
     * $12.50?») en vez de guardar en silencio o tirar la fila. Lo que no se
     * puede leer («doce») se pide escribir ahí mismo.
     */
    const leerPrecio = (campo: 'netPrice' | 'grossPrice'): number | undefined | 'pregunta' => {
      if (!cols.includes(campo)) return undefined;
      const raw = celda(row, cols, campo);
      if (raw === '') return undefined;
      if (correccion.precio != null) return correccion.precio;
      if (!precioConTexto(raw)) return parseImportNumber(raw);
      problemas.push({
        row: numero,
        name,
        message: parsePrecioLibre(raw) != null ? `En el precio dice «${raw}».` : `No entiendo el precio «${raw}».`,
        fix: { kind: 'precio', raw, value: parsePrecioLibre(raw) },
      });
      return 'pregunta';
    };
    const netoLeido = leerPrecio('netPrice');
    if (netoLeido === 'pregunta') return;
    const brutoLeido = leerPrecio('grossPrice');
    if (brutoLeido === 'pregunta') return;
    const netoRaw = netoLeido;
    const brutoRaw = brutoLeido;
    const tieneNeto = netoRaw !== undefined && netoRaw > 0;
    const tieneBruto = brutoRaw !== undefined && brutoRaw > 0;

    if (tieneNeto && tieneBruto) {
      problemas.push({
        row: numero,
        message: `"${name}": has rellenado «Precio sin IVA» y «Precio con IVA». Deja solo el que tengas delante: si los dos no cuadran, el que guardáramos sería un dato falso.`,
      });
      return;
    }
    if ((!tieneNeto && celda(row, cols, 'netPrice').trim() !== '') ||
        (!tieneBruto && celda(row, cols, 'grossPrice').trim() !== '')) {
      problemas.push({ row: numero, message: `"${name}": el precio de compra debe ser un número mayor que 0; déjalo vacío si aún no lo sabes.` });
      return;
    }
    if (tieneBruto && vatRaw === undefined) {
      problemas.push({
        row: numero,
        message: `"${name}": con el precio con IVA hace falta la columna «IVA %» rellena para poder calcular el precio sin IVA.`,
      });
      return;
    }

    /*
     * Sin columna de IVA, 0: es el IVA que le cobra el PROVEEDOR en esa
     * compra, y no lo sabemos. Inventarse un 10 % hacia que la ficha ensenyara
     * un "precio con IVA" que nadie habia tecleado. No toca el coste: los
     * escandallos van siempre con el neto, que es lo unico que se guarda.
     */
    const vatPct = vatRaw ?? 0;
    if (vatPct < 0 || vatPct > 100) {
      problemas.push({
        row: numero,
        message: `«IVA %» debe estar entre 0 y 100. Ahora contiene ${vatPct}.`,
      });
      return;
    }
    const netPrice = tieneNeto ? netoRaw! : tieneBruto ? netFromGross(brutoRaw!, vatPct) : undefined;

    const brand = cols.includes('brand') ? celda(row, cols, 'brand') || undefined : undefined;
    if (brand && brand.length > 80) {
      problemas.push({
        row: numero,
        message: `«Marca» tiene ${brand.length} caracteres; el máximo es 80. Acorta el texto.`,
      });
      return;
    }
    const clave = ingredientKey(name, brand);

    /*
     * Identidad: primero el Id Alexia (viene de la exportacion y sobrevive a
     * un cambio de nombre en el Excel), y si no, nombre + marca. Un id que ya
     * no existe (ingrediente borrado) se ignora y la fila entra como nueva.
     */
    const idCelda = cols.includes('id') ? celda(row, cols, 'id') : '';
    const porIdExistente = idCelda ? porId.get(idCelda) : undefined;
    const identidad = porIdExistente ? `id:${porIdExistente.ingredientId}` : clave;
    if (vistos.has(identidad)) {
      problemas.push({
        row: numero,
        message: `"${name}" está repetido en el fichero: solo se importa la primera vez.`,
      });
      return;
    }
    vistos.add(identidad);

    const supplierName = cols.includes('supplier')
      ? celda(row, cols, 'supplier') || undefined
      : undefined;
    if (supplierName && supplierName.length > 120) {
      problemas.push({
        row: numero,
        message: `«Proveedor» tiene ${supplierName.length} caracteres; el máximo es 120. Acorta el nombre.`,
      });
      return;
    }
    if (supplierName && !proveedoresConocidos.has(normalizeText(supplierName))) {
      proveedoresNuevos.add(supplierName);
    }

    const presentation = cols.includes('presentation')
      ? celda(row, cols, 'presentation') || undefined
      : undefined;
    if (presentation && presentation.length > 120) {
      problemas.push({
        row: numero,
        message: `«Presentación» tiene ${presentation.length} caracteres; el máximo es 120. Acorta el texto.`,
      });
      return;
    }

    const group = parseIngredientGroup(cols.includes('group') ? celda(row, cols, 'group') : '') ?? 'otros';
    if (group.length > 40) {
      problemas.push({
        row: numero,
        message: `«Grupo» tiene ${group.length} caracteres; el máximo es 40. Acorta el nombre del grupo.`,
      });
      return;
    }

    const densityGPerMl = parseImportNumber(celda(row, cols, 'density'));
    if (densityGPerMl !== undefined && densityGPerMl <= 0) {
      problemas.push({
        row: numero,
        message: '«Densidad» debe ser mayor que 0. Corrige el valor o deja la celda vacía si no la necesitas.',
      });
      return;
    }

    const draft: IngredientDraft = {
      row: numero,
      name,
      brand,
      presentation,
      group,
      supplierName,
      purchaseQty,
      purchaseUnit,
      netPrice,
      pricePending: netPrice === undefined,
      vatPct,
      densityGPerMl,
    };

    // Por id (la exportación) se actualiza sin preguntar: es «Cambiar precios».
    if (porIdExistente) {
      actualizados.push({ ...draft, existingId: porIdExistente.ingredientId });
      return;
    }

    const porNombreExacto = porClave.get(clave);
    if (porNombreExacto) {
      const cambiaPrecio = precioDistinto(porNombreExacto, draft);
      if (correccion.existente === 'crear') nuevos.push(draft);
      else if (correccion.existente === 'actualizar' || !cambiaPrecio) actualizados.push({ ...draft, existingId: porNombreExacto.ingredientId });
      else {
        problemas.push({
          row: numero,
          name,
          message: `Ya tienes «${porNombreExacto.name}».`,
          fix: {
            kind: 'existe',
            existingId: porNombreExacto.ingredientId,
            existingName: porNombreExacto.name,
            existing: { netPrice: porNombreExacto.netPrice, purchaseQty: porNombreExacto.purchaseQty, purchaseUnit: porNombreExacto.purchaseUnit, presentation: porNombreExacto.presentation },
            newPrice: draft.netPrice,
            newQty: draft.purchaseQty,
            newUnit: draft.purchaseUnit,
          },
        });
      }
      return;
    }

    /*
     * Fase 7: un nombre que no es igual pero se parece («Cebollas blancas»
     * y tu «Cebolla blanca»), o que es un alias que ya recordaste. El alias
     * entra solo; el parecido se pregunta: «¿Es tu …?».
     */
    if (correccion.parecido === 'crear') { nuevos.push(draft); return; }
    const elegido = correccion.parecido ? porId.get(correccion.parecido) : undefined;
    if (elegido) {
      actualizados.push({ ...draft, name: elegido.name, existingId: elegido.ingredientId, aliasDe: name });
      return;
    }
    const match = matchIngredient(name, existentes);
    const primero = match.kind === 'exact' || match.kind === 'fuzzy' ? match.candidates[0] : undefined;
    const unico = primero ? porId.get(primero.ingredientId) : undefined;
    // Solo un alias exacto (sin proveedor) actualiza el precio sin preguntar;
    // el de un proveedor se pregunta como cualquier parecido.
    if (unico && primero?.alias && match.kind === 'exact') {
      actualizados.push({ ...draft, name: unico.name, existingId: unico.ingredientId });
      return;
    }
    if (unico) {
      problemas.push({
        row: numero,
        name,
        message: `No lo encuentro escrito así.`,
        fix: { kind: 'parecido', raw: name, candidates: [{ ingredientId: unico.ingredientId, name: unico.name }] },
      });
      return;
    }
    nuevos.push(draft);
  });

  return {
    nuevos,
    actualizados,
    proveedoresNuevos: [...proveedoresNuevos],
    problemas,
    columnasNoEncontradas: [],
  };
}

/* ------------------------------------------------------------------ */
/* Recetas                                                             */
/* ------------------------------------------------------------------ */

/**
 * Referencia a una receta que se crea en ESTE MISMO fichero y que, por tanto,
 * todavia no tiene id. La resuelve el servidor al importar, cuando ya ha
 * creado las recetas del lote.
 */
export interface RecipeOfFileRef {
  kind: 'recipe_del_fichero';
  /** Nombre de la receta tal y como aparece en el Excel. */
  recipeName: string;
}

export type DraftRef = RecipeComponent['ref'] | RecipeOfFileRef | { kind: 'ingredient_del_fichero'; ingredientName: string };

export interface RecipeComponentDraft extends Omit<RecipeComponent, 'ref'> {
  /** Nombre tal y como viene en el fichero. */
  importedName: string;
  /** Ingrediente o subreceta con el que se ha emparejado. */
  ref?: DraftRef;
  /** Fila del Excel, para poder aplicar un arreglo a esta línea. */
  row?: number;
  /** Fase 7: algo por contestar en esta línea; la receta no se guarda hasta entonces. */
  fix?: ImportFix;
}

/** Donde hay que buscar el nombre de una linea. Lo dice la columna "Bloque". */
export type DondeBuscar = 'ingrediente' | 'receta' | 'cualquiera';

export interface ComponenteSinEmparejar {
  /** Nombre tal y como viene en el fichero. */
  name: string;
  /** Donde lo hemos buscado, para que el aviso mande al sitio correcto. */
  buscadoEn: DondeBuscar;
}

/**
 * La columna "Bloque" dice DOS cosas y hasta ahora solo se leia una:
 *
 *  - en que bloque de la receta va la linea (ingredientes o empaque), y
 *  - cuando pone "subreceta", DONDE hay que buscar el nombre.
 *
 * Sin lo segundo, "Sofrito base" con Bloque = subreceta se buscaba entre los
 * ingredientes, no aparecia, y el aviso mandaba al usuario a dar de alta un
 * ingrediente que no existe (bug que encontro Alex el 1 de septiembre).
 */
export function parseBloque(raw: string): { block: ComponentBlock; buscarEn: DondeBuscar } {
  const b = normalizeText(raw);
  // El empaque siempre es un producto del catalogo: una caja, una bolsa.
  if (b.startsWith('empaque') || b.startsWith('envase')) {
    return { block: 'empaque', buscarEn: 'ingrediente' };
  }
  if (b.startsWith('sub') || b.startsWith('receta') || b.startsWith('elaboracion') || b.startsWith('prepara')) {
    return { block: 'ingrediente', buscarEn: 'receta' };
  }
  if (b.startsWith('ingrediente') || b.startsWith('producto') || b.startsWith('materia')) {
    return { block: 'ingrediente', buscarEn: 'ingrediente' };
  }
  // Bloque vacio o desconocido: se busca donde sea, como se hacia siempre.
  return { block: 'ingrediente', buscarEn: 'cualquiera' };
}

export interface RecipeDraft {
  name: string;
  code?: string;
  kind: Recipe['kind'];
  group?: string;
  yieldQty: number;
  yieldUnit: Unit;
  portions: number;
  safetyMarginPct: number;
  /**
   * true  -> el coste de referencia va por unidad de rendimiento (por litro de
   *          salsa), que es lo que necesita una subreceta usada en otras.
   * false -> por racion, que es lo normal en un plato terminado.
   */
  useYieldCostAsBase: boolean;
  steps?: string;
  /** Precio de venta CON IVA (plantilla de Alex): con él, el plato entra en la carta con su precio. */
  salePrice?: number;
  components: RecipeComponentDraft[];
  existingId?: string;
  /** Fase 7: el archivo no dice si se vende y hay que preguntarlo. */
  kindFix?: ImportFix;
}

/** Una receta del Excel está lista si todo está emparejado y no queda nada por contestar. */
export const recipeDraftReady = (r: RecipeDraft): boolean =>
  !r.kindFix && r.components.every((c) => c.ref && !c.fix);

/**
 * La unidad más lógica para una cantidad sin unidad («180 de carne molida»):
 * la familia del ingrediente si lo conocemos, y gramos o piezas si no.
 */
export function unidadPropuesta(qty: number, purchaseUnit?: Unit): Unit {
  const familia = purchaseUnit ? dimensionOf(purchaseUnit) : undefined;
  if (familia === 'mass') return qty >= 10 ? 'g' : 'kg';
  if (familia === 'volume') return qty >= 10 ? 'ml' : 'l';
  if (familia === 'count') return 'pz';
  return qty >= 10 ? 'g' : 'pz';
}

export interface RecipeImportResult {
  ingredientStubs?: IngredientInput[];
  nuevas: RecipeDraft[];
  actualizadas: RecipeDraft[];
  /** Componentes que no hemos encontrado, con el sitio donde los buscamos. */
  sinEmparejar: ComponenteSinEmparejar[];
  problemas: ImportIssue[];
  columnasNoEncontradas: RecipeField[];
}

/**
 * Una receta ocupa VARIAS filas: una por linea de escandallo. Se agrupan por
 * el nombre de la receta, y los datos de cabecera (rendimiento, raciones...)
 * se leen de la primera fila que los traiga — asi el usuario puede repetirlos
 * en cada fila o dejarlos solo en la primera, que es como se rellena a mano.
 *
 * DOS PASADAS, y es lo importante de esta funcion. La primera lee el fichero
 * entero y apunta que recetas se van a crear; la segunda resuelve cada linea
 * contra el catalogo de ingredientes o contra esa lista. Con una sola pasada,
 * una linea que apuntaba a una subreceta creada mas abajo en el mismo Excel
 * no la encontraba: asi entraron 9 de 11 subrecetas de Alex y fallaron las
 * dos que usaban "Sofrito base", que se creaba en ese mismo fichero.
 *
 * El orden de las filas deja de importar, que es como tiene que ser: nadie
 * ordena su Excel por dependencias, y con tres niveles (Ragu -> Salsa de
 * tomate -> Sofrito base) hay casos en los que ni siquiera se podria.
 */
export function buildRecipeImport(
  rows: string[][],
  cols: Array<RecipeField | undefined>,
  ingredientes: Array<Pick<Ingredient, 'ingredientId' | 'name' | 'brand'> & Partial<Pick<Ingredient, 'purchaseUnit' | 'aliases'>>> = [],
  recetas: Array<Pick<Recipe, 'recipeId' | 'name'>> = [],
  correcciones: RecipeCorrections = {},
  opcionesCantidad: OpcionesCantidad = {},
): RecipeImportResult {
  const problemas: ImportIssue[] = [];
  const sinEmparejar = new Map<string, ComponenteSinEmparejar>();
  const stubs = new Map<string, IngredientInput>();

  const obligatorias: RecipeField[] = ['recipeName', 'componentName', 'qty', 'unit'];
  const columnasNoEncontradas = obligatorias.filter((f) => !cols.includes(f));
  if (columnasNoEncontradas.length > 0) {
    return { nuevas: [], actualizadas: [], sinEmparejar: [], problemas, columnasNoEncontradas };
  }

  // Indice de busqueda: por nombre+marca, por nombre solo y por receta.
  const porNombreMarca = new Map(ingredientes.map((i) => [ingredientKey(i.name, i.brand), i]));
  const porNombre = new Map<string, { ingredientId: string }>();
  for (const ing of ingredientes) {
    const k = normalizeText(ing.name);
    // Con dos marcas del mismo producto no adivinamos: se queda sin emparejar.
    if (porNombre.has(k)) porNombre.set(k, { ingredientId: '' });
    else porNombre.set(k, { ingredientId: ing.ingredientId });
  }
  const recetasPorNombre = new Map(recetas.map((r) => [normalizeText(r.name), r]));

  const borradores = new Map<string, RecipeDraft>();
  /** Recetas cuya fila no dice si es plato o preparación (columna «Tipo» vacía). */
  const sinTipo = new Set<string>();
  const filaDeReceta = new Map<string, number>();

  /* --- Pasada 1: que recetas trae el fichero --------------------------- */

  rows.forEach((row, index) => {
    const numero = index + 2;
    const recipeName = celda(row, cols, 'recipeName');
    const componentName = celda(row, cols, 'componentName');
    if (recipeName === '' && componentName === '') return;

    if (recipeName === '') {
      problemas.push({
        row: numero,
        message: `Falta el nombre de la receta a la que pertenece «${componentName}».`,
      });
      return;
    }

    const clave = normalizeText(recipeName);
    if (borradores.has(clave)) return;

    const kindRaw = normalizeText(celda(row, cols, 'kind'));
    const existente = recetasPorNombre.get(clave);
    const kindElegido = correcciones.tipos?.[clave];
    const kind: Recipe['kind'] = kindElegido ?? (kindRaw.startsWith('sub') || kindRaw.startsWith('prepara') ? 'subreceta' : 'plato');
    const precio = cols.includes('salePrice') ? parseImportNumber(celda(row, cols, 'salePrice')) : undefined;
    const salePrice = precio != null && precio > 0 ? precio : undefined;
    // «Si tiene precio de venta, Plato» (reglas de la plantilla de Alex): no se pregunta.
    if (kindRaw === '' && !kindElegido && !existente && salePrice == null) sinTipo.add(clave);
    filaDeReceta.set(clave, numero);
    borradores.set(clave, {
      name: recipeName,
      code: celda(row, cols, 'code') || undefined,
      kind,
      group: celda(row, cols, 'group') || undefined,
      /*
       * Sin columna, la SUBRECETA se valora por rendimiento y el plato por
       * racion. Antes entraba todo por racion, y eso rompia a la receta padre
       * en cuanto pedia "300 g de sofrito": una subreceta valorada por racion
       * solo admite lineas en piezas (ver computeRecipeCost). Es de donde
       * salian los "Revisar" del import masivo.
       */
      useYieldCostAsBase:
        parseSiNo(cols.includes('yieldCostBase') ? celda(row, cols, 'yieldCostBase') : '') ??
        kind === 'subreceta',
      yieldQty: parseImportNumber(celda(row, cols, 'yieldQty')) ?? 1,
      yieldUnit: parseUnit(celda(row, cols, 'yieldUnit')) ?? 'pz',
      portions: Math.max(1, Math.round(parseImportNumber(celda(row, cols, 'portions')) ?? 1)),
      safetyMarginPct: parseImportNumber(celda(row, cols, 'safetyMarginPct')) ?? 10,
      steps: celda(row, cols, 'steps') || undefined,
      salePrice: kind === 'plato' ? salePrice : undefined,
      components: [],
      existingId: existente?.recipeId,
    });
  });

  /**
   * Busca el nombre donde toque y devuelve la referencia, o undefined.
   *
   * Una receta del fichero que TODAVIA no existe se devuelve como
   * `recipe_del_fichero`: el id no lo sabemos aqui, lo pone el servidor
   * cuando ya la ha creado.
   */
  const resolver = (nombre: string, buscarEn: DondeBuscar): DraftRef | undefined => {
    const clave = normalizeText(nombre);

    const comoReceta = (): DraftRef | undefined => {
      const enBase = recetasPorNombre.get(clave);
      if (enBase) return { kind: 'recipe', recipeId: enBase.recipeId };
      const delFichero = borradores.get(clave);
      // Si ya existia en base de datos, el borrador trae su id y se usa ese.
      if (delFichero) {
        return delFichero.existingId
          ? { kind: 'recipe', recipeId: delFichero.existingId }
          : { kind: 'recipe_del_fichero', recipeName: delFichero.name };
      }
      return undefined;
    };

    const comoIngrediente = (): DraftRef | undefined => {
      const exacto = porNombreMarca.get(ingredientKey(nombre));
      if (exacto) return { kind: 'ingredient', ingredientId: exacto.ingredientId };
      const soloNombre = porNombre.get(clave);
      // ingredientId vacio = dos marcas del mismo producto: no adivinamos.
      if (soloNombre && soloNombre.ingredientId !== '') {
        return { kind: 'ingredient', ingredientId: soloNombre.ingredientId };
      }
      return undefined;
    };

    if (buscarEn === 'receta') return comoReceta();
    if (buscarEn === 'ingrediente') return comoIngrediente();
    return comoIngrediente() ?? comoReceta();
  };

  /* --- Pasada 2: resolver cada linea ----------------------------------- */

  rows.forEach((row, index) => {
    const numero = index + 2;
    const recipeName = celda(row, cols, 'recipeName');
    const componentName = celda(row, cols, 'componentName');
    // Fila vacia, fila huerfana (ya avisada) o cabecera de receta sin linea.
    if (recipeName === '' || componentName === '') return;

    const draft = borradores.get(normalizeText(recipeName));
    if (!draft) return;

    const correccion = correcciones.filas?.[numero] ?? {};
    const nombreFila = `${recipeName} · ${componentName}`;
    const qtyCelda = celda(row, cols, 'qty');
    const dudaQty = correccion.qty == null ? dudaDeCantidad(qtyCelda, opcionesCantidad) : undefined;
    if (dudaQty) {
      problemas.push({ row: numero, name: nombreFila, message: dudaQty.message, fix: dudaQty.fix });
      return;
    }
    const qty = correccion.qty ?? leerCantidadDe(qtyCelda, opcionesCantidad);
    if (qty === undefined || qty <= 0) {
      problemas.push({
        row: numero,
        name: nombreFila,
        message: `"${recipeName}" → «${componentName}»: la cantidad tiene que ser mayor que 0.`,
      });
      return;
    }

    const { block, buscarEn } = parseBloque(celda(row, cols, 'block'));
    let ref: DraftRef | undefined;
    let fix: ImportFix | undefined;
    const elegido = correccion.ingredientId ? ingredientes.find((i) => i.ingredientId === correccion.ingredientId) : undefined;
    if (elegido) ref = { kind: 'ingredient', ingredientId: elegido.ingredientId };
    else if (!correccion.crear) ref = resolver(componentName, buscarEn);
    const stub = (purchaseUnitDe: Unit) => {
      const key = normalizeIngredientName(componentName);
      const previous = stubs.get(key);
      const purchaseUnit = dimensionOf(purchaseUnitDe) === 'mass' ? 'kg' : dimensionOf(purchaseUnitDe) === 'volume' ? 'l' : 'pz';
      if (!previous) stubs.set(key, { name: componentName, group: 'otros',
        purchaseQty: 1, purchaseUnit, vatPct: 0, pricePending: true, createdVia: 'importacion', active: true });
      return { kind: 'ingredient_del_fichero' as const, ingredientName: previous?.name ?? componentName };
    };

    // La unidad: la del archivo, la que eligió en la previa, o se pregunta.
    const unidadCelda = celda(row, cols, 'unit');
    const ingredienteDeRef = ref?.kind === 'ingredient' ? ingredientes.find((i) => i.ingredientId === (ref as { ingredientId: string }).ingredientId) : undefined;
    let unit = correccion.unit ?? parseUnit(unidadCelda);
    if (!unit) {
      const propuesta = unidadPropuesta(qty, ingredienteDeRef?.purchaseUnit);
      fix = unidadCelda === ''
        ? { kind: 'cantidad', raw: qtyCelda, qty, unit: propuesta }
        : { kind: 'unidad', raw: unidadCelda, options: ['g', 'kg', 'ml', 'l', 'pz'] };
      problemas.push({
        row: numero,
        name: nombreFila,
        message: unidadCelda === '' ? `Dice «${qtyCelda}», sin unidad.` : `No entiendo la unidad «${unidadCelda}».`,
        fix,
      });
      unit = propuesta;
    }

    if (!ref && buscarEn !== 'receta') {
      const match = correccion.crear ? { kind: 'none' as const, candidates: [] } : matchIngredient(componentName, ingredientes);
      if (match.kind === 'exact' || match.kind === 'fuzzy') {
        ref = { kind: 'ingredient', ingredientId: match.candidates[0].ingredientId };
      } else if (match.kind === 'none') {
        ref = stub(unit);
      } else if (!fix) {
        // Varios parecidos: se pregunta «¿Es tu …?» con los tres primeros.
        fix = { kind: 'parecido', raw: componentName, candidates: match.candidates.slice(0, 3).map((c) => ({ ingredientId: c.ingredientId, name: c.name })) };
        problemas.push({ row: numero, name: nombreFila, message: 'No la encuentro escrita así.', fix });
      }
    }
    if (!ref && !fix) sinEmparejar.set(normalizeText(componentName), { name: componentName, buscadoEn: buscarEn });

    draft.components.push({
      importedName: componentName,
      ref,
      block,
      qty,
      unit,
      wastePct: parseImportNumber(celda(row, cols, 'wastePct')) ?? 0,
      row: numero,
      ...(fix ? { fix } : {}),
    });
  });

  /*
   * Fase 7 · «No dice si la vendes o es una preparación». Si la usan otras
   * recetas de este mismo archivo, parece una preparación: se propone y se
   * pregunta. Si no la usa nadie, es un plato, como siempre.
   */
  for (const clave of sinTipo) {
    const draft = borradores.get(clave);
    if (!draft) continue;
    const usadaPor = [...borradores.values()].filter((otra) => otra !== draft && otra.components.some((c) =>
      (c.ref?.kind === 'recipe_del_fichero' && normalizeText(c.ref.recipeName) === clave)
      || (c.ref?.kind === 'recipe' && draft.existingId && c.ref.recipeId === draft.existingId))).length;
    if (usadaPor === 0) continue;
    draft.kind = 'subreceta';
    draft.useYieldCostAsBase = parseSiNo(cols.includes('yieldCostBase') ? celda(rows[(filaDeReceta.get(clave) ?? 2) - 2] ?? [], cols, 'yieldCostBase') : '') ?? true;
    draft.kindFix = { kind: 'tipo', recipeName: draft.name, suggested: 'subreceta', usadaPor };
    problemas.push({
      row: filaDeReceta.get(clave) ?? 0,
      name: draft.name,
      message: `No dice si la vendes o es una preparación. La ${usadaPor === 1 ? 'usa 1 plato' : `usan ${usadaPor} platos`} de este mismo archivo, así que parece una preparación.`,
      fix: draft.kindFix,
    });
  }

  const todas = [...borradores.values()].filter((d) => {
    if (d.components.length === 0) {
      problemas.push({ row: 0, message: `"${d.name}" no tiene ninguna línea: se salta.` });
      return false;
    }
    return true;
  });

  return {
    nuevas: todas.filter((d) => !d.existingId),
    actualizadas: todas.filter((d) => d.existingId),
    sinEmparejar: [...sinEmparejar.values()],
    ingredientStubs: [...stubs.values()],
    problemas,
    columnasNoEncontradas: [],
  };
}

/* ------------------------------------------------------------------ */
/* Plantilla de ejemplo                                                */
/* ------------------------------------------------------------------ */

/** Cabeceras oficiales, las que lleva la plantilla que se descarga. */
export const INGREDIENT_TEMPLATE_HEADERS = [
  'Producto',
  'Marca',
  'Grupo',
  'Proveedor',
  'Presentación',
  'Cantidad de compra',
  'Unidad de compra',
  // Se rellena UNA de las dos, la que tenga delante quien la rellena. Con las
  // dos puestas la fila no se importa: ver `buildIngredientImport`.
  'Precio sin IVA',
  'Precio con IVA',
  'IVA %',
  'Densidad g/ml',
];

/**
 * Cabeceras de la EXPORTACION de ingredientes: la plantilla mas la columna
 * "Id Alexia" al final. Es el ciclo completo de actualizacion de precios:
 * descargas lo que tienes, cambias los precios en Excel y lo resubes por el
 * importador de siempre, que con el id actualiza aunque renombres productos.
 */
export const INGREDIENT_EXPORT_HEADERS = [...INGREDIENT_TEMPLATE_HEADERS, 'Id Alexia'];

/** Filas de la exportacion, en el orden exacto de INGREDIENT_EXPORT_HEADERS. */
export function ingredientExportRows(
  ingredients: Array<
    Pick<
      Ingredient,
      | 'ingredientId'
      | 'name'
      | 'brand'
      | 'presentation'
      | 'group'
      | 'supplierId'
      | 'purchaseQty'
      | 'purchaseUnit'
      | 'netPrice'
      | 'prices'
      | 'vatPct'
      | 'densityGPerMl'
      | 'active'
    >
  >,
  suppliers: Array<{ supplierId: string; name: string }> = [],
): Array<Array<string | number>> {
  const proveedorDe = new Map(suppliers.map((s) => [s.supplierId, s.name]));
  const numero = (value: number | undefined): string | number => value ?? '';

  return ingredients
    .filter((ing) => ing.active)
    .sort((a, b) => a.name.localeCompare(b.name, 'es'))
    .map((ing) => [
      ing.name,
      ing.brand ?? '',
      // La etiqueta bonita: el importador la reconoce y la vuelve a su clave.
      ingredientGroupLabel(ing.group),
      (ing.supplierId && proveedorDe.get(ing.supplierId)) || '',
      ing.presentation ?? '',
      ing.purchaseQty ?? '',
      ing.purchaseUnit ? UNITS[ing.purchaseUnit].label : '',
      numero(netPriceOf(ing)),
      // "Precio con IVA" sale VACIA a proposito: si viniera rellena, resubir
      // el fichero sin tocarlo chocaria con la regla de "las dos no".
      '',
      ing.vatPct,
      numero(ing.densityGPerMl),
      ing.ingredientId,
    ]);
}

export const RECIPE_TEMPLATE_HEADERS = [
  'Receta',
  'Código',
  'Tipo',
  'Grupo de carta',
  // 1 = el coste va por unidad de rendimiento (por litro de salsa); 0 = por
  // ración. Vacío: las subrecetas por rendimiento, los platos por ración.
  'Coste por rendimiento',
  'Rendimiento',
  'Unidad de rendimiento',
  'Porciones',
  'Margen de seguridad %',
  'Ingrediente',
  'Cantidad neta',
  'Unidad',
  'Merma %',
  'Bloque',
];

/*
 * La segunda fila rellena "Precio con IVA" y deja vacio el neto: es el
 * ejemplo de que se puede teclear lo que pone el tique y Alexia saca el neto.
 */
export const INGREDIENT_TEMPLATE_ROWS: string[][] = [
  ['Aceite de oliva virgen extra', 'Oro Bailén', 'Aceites', 'Distribuciones del Norte', 'garrafón', '5', 'L', '42,50', '', '10', ''],
  ['Harina de trigo', 'Molino Real', 'Secos y granos', 'Distribuciones del Norte', 'saco', '25', 'kg', '', '17,88', '10', ''],
  ['Solomillo de ternera', '', 'Carnes y pescados', 'Carnes Roma', 'granel', '1', 'kg', '28,90', '', '10', ''],
];

export const RECIPE_TEMPLATE_ROWS: string[][] = [
  ['Croquetas caseras', 'PIC001', 'plato', 'Entrantes', '0', '12', 'pz', '2', '10', 'Harina de trigo', '250', 'g', '0', 'ingrediente'],
  ['Croquetas caseras', '', '', '', '', '', '', '', '', 'Aceite de oliva virgen extra', '50', 'ml', '0', 'ingrediente'],
  ['Solomillo al pesto', 'PRI001', 'plato', 'Principales', '0', '1', 'pz', '1', '10', 'Solomillo de ternera', '220', 'g', '8', 'ingrediente'],
  // Una subreceta que rinde 2 L: se cuesta por litro, y por eso los platos que
  // la usan pueden pedirle "300 ml" en vez de raciones.
  ['Salsa de tomate', 'SUB001', 'subreceta', '', '1', '2', 'l', '1', '10', 'Tomate pera', '2500', 'g', '10', 'ingrediente'],
];
